import type {
  GameSessionReviewStatus,
  GameSessionStatus,
} from '../../assets/enums/game-session.enum.ts';
import { type ChallengeMode } from '../challenge-configs-api.ts';
import type { IsoDate, Nullable } from '../types.ts';
import { type ClientPerformanceAggregation } from './client-aggregation/index.ts';

/**
 * Contract of the future performance aggregation service (`GET /performance`).
 *
 * Until that service exists, the dashboard is aggregated on the client by `client-aggregation/`.
 * When the endpoint ships, call it here and delete that folder.
 */
export class PerformanceApi {
  constructor(private clientAggregation: ClientPerformanceAggregation) {}

  public async getDashboard(params: PerformanceDashboardParams): Promise<PerformanceDashboard> {
    return this.clientAggregation.getDashboard(params);
  }
}

export type PerformancePeriod =
  | 'this_month'
  | 'last_month'
  | 'last_7_days'
  | 'last_30_days'
  | 'last_6_months';

export type PerformanceBucketSize = 'day' | 'month';

export type PerformanceDashboardParams = {
  period: PerformancePeriod;
  time_zone: string;
  region_id?: number;
  partner_id?: string;
  course_id?: string;
};

/**
 * Money is in dollars. Percentages are 0–100 and null when there is nothing to divide by.
 * Every block covers completed (`completed`, `paid_out`) non-demo sessions unless noted.
 */
export type PerformanceTotals = {
  sessions: number;
  won: number;
  wagers: number;
  payouts: number;
  net: number;
  win_rate: Nullable<number>;
  /** Payouts / wagers. Above 100 the period lost money. */
  payout_ratio: Nullable<number>;
};

export type PerformanceKpis = PerformanceTotals & {
  players: number;
};

export type PerformanceTrendBucket = PerformanceTotals & {
  start: IsoDate;
};

export type PerformancePartner = {
  id: string;
  name: string;
  logo: Nullable<string>;
};

export type PerformancePartnerRow = PerformanceTotals & {
  /** Null groups sessions whose course is unknown. */
  partner: Nullable<PerformancePartner>;
  share: Nullable<number>;
};

export type PerformanceGameRow = PerformanceTotals & {
  config_id: number;
  name: string;
  mode: Nullable<ChallengeMode>;
};

export type PerformanceStuckGroup = {
  count: number;
  older_than_30_days: number;
  potential_payout: number;
};

export type PerformanceStuckSession = {
  id: string;
  status: GameSessionStatus;
  game_name: string;
  created_at: IsoDate;
  age_days: number;
  potential_payout: number;
};

export type PerformanceStuckSessions = {
  active: PerformanceStuckGroup;
  awaiting_payment: PerformanceStuckGroup;
  oldest: PerformanceStuckSession[];
};

export type PerformanceReviewAgeBucket =
  | 'under_1_day'
  | 'from_1_to_7_days'
  | 'from_7_to_30_days'
  | 'over_30_days';

export type PerformanceReviewQueue = {
  awaiting: number;
  oldest_completed_at: Nullable<IsoDate>;
  aging: { bucket: PerformanceReviewAgeBucket; count: number }[];
  reviewed: number;
  median_review_hours: Nullable<number>;
  p90_review_hours: Nullable<number>;
  won_awaiting: number;
  won_awaiting_payouts: number;
};

export type PerformanceMlAgreement = {
  decided: number;
  matched: number;
  match_rate: Nullable<number>;
  won_won: number;
  lost_lost: number;
  won_lost: number;
  lost_won: number;
  no_decision: number;
};

export type PerformanceTopPlayer = {
  player_id: string;
  name: Nullable<string>;
  sessions: number;
  wagers: number;
  payouts: number;
};

export type PerformanceTopPlayers = {
  unique: number;
  rows: PerformanceTopPlayer[];
};

export type PerformanceTopWin = {
  session_id: string;
  player_id: string;
  player_name: Nullable<string>;
  game_name: string;
  wager: number;
  payout: number;
  created_at: IsoDate;
  review_status: GameSessionReviewStatus;
};

export type PerformanceDashboard = {
  period: { from: IsoDate; to: IsoDate; bucket: PerformanceBucketSize };
  kpis: PerformanceKpis;
  trend: PerformanceTrendBucket[];
  partners: PerformancePartnerRow[];
  games: PerformanceGameRow[];
  stuck_sessions: PerformanceStuckSessions;
  review_queue: PerformanceReviewQueue;
  ml_agreement: PerformanceMlAgreement;
  top_players: PerformanceTopPlayers;
  top_wins: PerformanceTopWin[];
};
